import React from 'react';
import { Card, CardBody, CardHeader, Row, Col, Form, FormGroup, Label, Input, Button, Spinner, Alert } from 'reactstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
    getEarnestDepositGlobalValues,
    createEarnestDepositGlobalValues,
    updateEarnestDepositGlobalValues
} from 'endpoints/builder/builderPropertyListingDetails';

interface EarnestDepositValues {
    inspection_report: number;
    rentAgreementPercentage: number;
    tax_percentage: number;
    identity_verification_percentage: number;
    transport_logistics_percentage: number;
    platform_fee_percentage: number;
}

// Validation schema
const validationSchema = Yup.object({
    inspection_report: Yup.number()
        .min(0, 'Must be non-negative')
        .required('Inspection report amount is required'),
    rentAgreementPercentage: Yup.number()
        .min(0, 'Must be non-negative')
        .max(100, 'Cannot exceed 100%')
        .required('Rent agreement percentage is required'),
    tax_percentage: Yup.number()
        .min(0, 'Must be non-negative')
        .max(100, 'Cannot exceed 100%')
        .required('Tax percentage is required'),
    identity_verification_percentage: Yup.number()
        .min(0, 'Must be non-negative')
        .max(100, 'Cannot exceed 100%')
        .required('Identity verification percentage is required'),
    transport_logistics_percentage: Yup.number()
        .min(0, 'Must be non-negative')
        .max(100, 'Cannot exceed 100%')
        .required('Transport & logistics percentage is required'),
    platform_fee_percentage: Yup.number()
        .min(0, 'Must be non-negative')
        .max(100, 'Cannot exceed 100%')
        .required('Platform fee percentage is required'),
});

const GlobalRentEarnestDeposit: React.FC = () => {
    const queryClient = useQueryClient();

    // Fetch existing global values
    const {
        data: globalData,
        isLoading: isFetchingData,
        isError: fetchError,
        error
    } = useQuery({
        queryKey: ['rentEarnestDepositGlobalValues'],
        queryFn: () => getEarnestDepositGlobalValues('rental'),
        staleTime: 0, // 5 minutes
        gcTime: 0,
        retry: 3,
    });

    // Create mutation
    const createMutation = useMutation({
        mutationFn: createEarnestDepositGlobalValues,
        onSuccess: () => {
            toast.success('Global earnest deposit values created successfully');
            queryClient.invalidateQueries({ queryKey: ['rentEarnestDepositGlobalValues'] });
        },
        onError: (error: any) => {
            console.error('Create failed:', error);
            toast.error('Failed to create global earnest deposit values');
        }
    });

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: EarnestDepositValues }) =>
            updateEarnestDepositGlobalValues(id, data),
        onSuccess: () => {
            toast.success('Global Rent Earnest Deposit values updated successfully');
            queryClient.invalidateQueries({ queryKey: ['rentEarnestDepositGlobalValues'] });
        },
        onError: (error: any) => {
            console.error('Update failed:', error);
            toast.error('Failed to update global Rent Earnest Deposit values');
        }
    });

    // Get initial values
    const getInitialValues = (): EarnestDepositValues => {
        const existingData = globalData?.data;
        return {
            inspection_report: existingData?.inspection_report || 0,
            rentAgreementPercentage: existingData?.rental_agreement || 0,
            tax_percentage: existingData?.tax_percentage || 0,
            identity_verification_percentage: existingData?.identity_verification_percentage || 0,
            transport_logistics_percentage: existingData?.transport_logistics_percentage || 0,
            platform_fee_percentage: existingData?.platform_fee_percentage || 0,
        };
    };

    // Formik form
    const formik = useFormik({
        initialValues: getInitialValues(),
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const existingData = globalData?.data;

            if (existingData && existingData.id) {
                // Update existing data
                updateMutation.mutate({
                    id: existingData.id,
                    data: values
                });
            } else {
                // Create new data
                createMutation.mutate({
                    ...values,
                    depositType: 'rental'
                });
            }
        },
    });

    // Check if form values have changed
    const hasFormChanged = () => {
        const initialValues = getInitialValues();
        return Object.keys(initialValues).some(
            key => formik.values[key as keyof EarnestDepositValues] !== initialValues[key as keyof EarnestDepositValues]
        );
    };

    // Reset form to initial values
    const handleReset = () => {
        formik.resetForm({
            values: getInitialValues()
        });
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;
    const existingData = globalData?.data;

    return (
        <Card>
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                        <i className="ri-settings-3-line me-2"></i>
                        Global Rent Earnest Deposit Configuration
                    </h5>
                    <div className="text-muted small">
                        {existingData ? (
                            <span className="badge bg-success">Configured</span>
                        ) : (
                            <span className="badge bg-warning">Not Configured</span>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardBody>
                {/* Loading State */}
                {isFetchingData && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading global earnest deposit configuration...</p>
                    </div>
                )}

                {/* Error State */}
                {fetchError && (
                    <Alert color="danger" className="mb-4">
                        <i className="ri-error-warning-line me-2"></i>
                        Failed to load configuration: {error?.message || 'Unknown error'}
                    </Alert>
                )}

                {/* Form Content */}
                {!isFetchingData && !fetchError && (
                    <>
                        <div className="mb-4">
                            <p className="text-muted">
                                Configure the global earnest deposit values that will be used as default values across the system.
                                These values can be customized per transaction in the Final Disclosure modal.
                            </p>
                        </div>

                        <Form onSubmit={formik.handleSubmit}>
                            <Row>
                                {/* Inspection Report */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="inspection_report" className="form-label">
                                            Inspection Report Amount ($)
                                            <span className="text-danger ms-1">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            id="inspection_report"
                                            name="inspection_report"
                                            value={formik.values.inspection_report}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            invalid={formik.touched.inspection_report && !!formik.errors.inspection_report}
                                        />
                                        {formik.touched.inspection_report && formik.errors.inspection_report && (
                                            <div className="text-danger small mt-1">{formik.errors.inspection_report}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Deed Recording Percentage */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="rentAgreementPercentage" className="form-label">
                                            Rent Agreement (%)
                                            <span className="text-danger ms-1">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            id="rentAgreementPercentage"
                                            name="rentAgreementPercentage"
                                            value={formik.values.rentAgreementPercentage}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            invalid={formik.touched.rentAgreementPercentage && !!formik.errors.rentAgreementPercentage}
                                        />
                                        {formik.touched.rentAgreementPercentage && formik.errors.rentAgreementPercentage && (
                                            <div className="text-danger small mt-1">{formik.errors.rentAgreementPercentage}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Tax Percentage */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="tax_percentage" className="form-label">
                                            Tax (%)
                                            <span className="text-danger ms-1">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            id="tax_percentage"
                                            name="tax_percentage"
                                            value={formik.values.tax_percentage}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            invalid={formik.touched.tax_percentage && !!formik.errors.tax_percentage}
                                        />
                                        {formik.touched.tax_percentage && formik.errors.tax_percentage && (
                                            <div className="text-danger small mt-1">{formik.errors.tax_percentage}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Identity Verification Percentage */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="identity_verification_percentage" className="form-label">
                                            Identity Verification (%)
                                            <span className="text-danger ms-1">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            id="identity_verification_percentage"
                                            name="identity_verification_percentage"
                                            value={formik.values.identity_verification_percentage}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            invalid={formik.touched.identity_verification_percentage && !!formik.errors.identity_verification_percentage}
                                        />
                                        {formik.touched.identity_verification_percentage && formik.errors.identity_verification_percentage && (
                                            <div className="text-danger small mt-1">{formik.errors.identity_verification_percentage}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Transport & Logistics Percentage */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="transport_logistics_percentage" className="form-label">
                                            Transport & Logistics (%)
                                            <span className="text-danger ms-1">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            id="transport_logistics_percentage"
                                            name="transport_logistics_percentage"
                                            value={formik.values.transport_logistics_percentage}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            invalid={formik.touched.transport_logistics_percentage && !!formik.errors.transport_logistics_percentage}
                                        />
                                        {formik.touched.transport_logistics_percentage && formik.errors.transport_logistics_percentage && (
                                            <div className="text-danger small mt-1">{formik.errors.transport_logistics_percentage}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Platform Fee Percentage */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="platform_fee_percentage" className="form-label">
                                            Platform Fee (%)
                                            <span className="text-danger ms-1">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            id="platform_fee_percentage"
                                            name="platform_fee_percentage"
                                            value={formik.values.platform_fee_percentage}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            invalid={formik.touched.platform_fee_percentage && !!formik.errors.platform_fee_percentage}
                                        />
                                        {formik.touched.platform_fee_percentage && formik.errors.platform_fee_percentage && (
                                            <div className="text-danger small mt-1">{formik.errors.platform_fee_percentage}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Action Buttons */}
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <Button
                                    type="button"
                                    color="danger"
                                    onClick={handleReset}
                                    disabled={isLoading || !hasFormChanged()}
                                >
                                    <i className="ri-refresh-line me-1"></i>
                                    Reset
                                </Button>
                                <Button
                                    type="submit"
                                    color="success"
                                    disabled={isLoading || !formik.isValid || !formik.dirty}
                                >
                                    {isLoading && <Spinner size="sm" className="me-2" />}
                                    <i className={`${isLoading ? '' : existingData ? 'ri-refresh-line' : 'ri-add-line'} me-1`}></i>
                                    {isLoading ? 'Saving...' : existingData ? 'Update Configuration' : 'Create Configuration'}
                                </Button>
                            </div>
                        </Form>

                        {/* Summary Card */}
                        {existingData && (
                            <div className="mt-4 p-3 bg-light rounded">
                                <h6 className="mb-3">
                                    <i className="ri-information-line me-2"></i>
                                    Current Configuration Summary
                                </h6>
                                <Row className="text-sm">
                                    <Col md={4}>
                                        <div className="mb-2">
                                            <strong>Inspection Report:</strong> ${existingData.inspection_report || 0}
                                        </div>
                                        <div className="mb-2">
                                            <strong>Rental Agreement:</strong> {existingData.rental_agreement || 0}%
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-2">
                                            <strong>Tax:</strong> {existingData.tax_percentage || 0}%
                                        </div>
                                        <div className="mb-2">
                                            <strong>Identity Verification:</strong> {existingData.identity_verification_percentage || 0}%
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-2">
                                            <strong>Transport & Logistics:</strong> {existingData.transport_logistics_percentage || 0}%
                                        </div>
                                        <div className="mb-2">
                                            <strong>Platform Fee:</strong> {existingData.platform_fee_percentage || 0}%
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default GlobalRentEarnestDeposit;
